<script setup lang="ts">
import { logger } from '@/lib/logger'
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { VueFlow, useVueFlow, Position, ConnectionLineType } from '@vue-flow/core'
import { useScenesStore, type Scene } from '@/stores/scenes'
import { useDisplaysStore } from '@/stores/displays'
import { useSpotifyNodesStore, type SpotifyNode } from '@/stores/spotifyNodes'
import { useMusicNodesStore, type MusicNode } from '@/stores/musicNodes'
import { usePlaylistNodesStore, type PlaylistNode } from '@/stores/playlistNodes'
import { useGraphContainersStore, type GraphContainer } from '@/stores/graphContainers'
import { useSpotifyStore } from '@/stores/spotify'
import { useAudioPlaybackStore } from '@/stores/audioPlayback'
import { useDisplayAudioStore } from '@/stores/displayAudio'
import { useSceneSpotifyConfigsStore } from '@/stores/sceneSpotifyConfigs'
import { useSceneMusicConfigsStore } from '@/stores/sceneMusicConfigs'
import { useScenePlaylistConfigsStore } from '@/stores/scenePlaylistConfigs'
import { parseSpotifyUrl, isValidSpotifyUrl } from '@/lib/spotify-utils'
import { useBreakpoints } from '@/composables/useBreakpoints'
import { useDialogs } from '@/composables/useDialog'
import { toast } from '@/components/ui/toast'
import type { Node, Edge, Connection, Position as VueFlowPosition } from '@vue-flow/core'

// Import components
import {
  SceneNode,
  SpotifyNode as SpotifyNodeComponent,
  MusicNode as MusicNodeComponent,
  PlaylistNode as PlaylistNodeComponent,
  ContainerNode as ContainerNodeComponent,
  GraphZoomControls,
  GraphContextMenu,
  GraphDialogs,
  GraphSearch
} from '@/components/scene-graph'

// Import Vue Flow styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'

// ============================================
// STORES & COMPOSABLES
// ============================================
const router = useRouter()
const scenesStore = useScenesStore()
const displaysStore = useDisplaysStore()
const spotifyNodesStore = useSpotifyNodesStore()
const musicNodesStore = useMusicNodesStore()
const playlistNodesStore = usePlaylistNodesStore()
const graphContainersStore = useGraphContainersStore()
const spotifyStore = useSpotifyStore()
const audioPlaybackStore = useAudioPlaybackStore()
const displayAudioStore = useDisplayAudioStore()
const sceneSpotifyConfigsStore = useSceneSpotifyConfigsStore()
const sceneMusicConfigsStore = useSceneMusicConfigsStore()
const scenePlaylistConfigsStore = useScenePlaylistConfigsStore()
const { fitView, zoomIn: vueFlowZoomIn, zoomOut: vueFlowZoomOut, project, setCenter, findNode, getViewport, setViewport, updateNodeInternals, getNodes } = useVueFlow()
const { isMobile } = useBreakpoints()

// ============================================
// GRAPH PERSISTENCE (localStorage)
// ============================================
const GRAPH_SETTINGS_KEY = 'scene-graph-settings'

interface GraphSettings {
  connectionMode: ConnectionMode
  viewport: { x: number; y: number; zoom: number } | null
}

const loadGraphSettings = (): GraphSettings => {
  try {
    const saved = localStorage.getItem(GRAPH_SETTINGS_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch (e) {
    console.warn('Failed to load graph settings:', e)
  }
  return { connectionMode: 'all', viewport: null }
}

const saveGraphSettings = () => {
  try {
    const viewport = getViewport()
    const settings: GraphSettings = {
      connectionMode: connectionMode.value,
      viewport: { x: viewport.x, y: viewport.y, zoom: viewport.zoom }
    }
    localStorage.setItem(GRAPH_SETTINGS_KEY, JSON.stringify(settings))
  } catch (e) {
    console.warn('Failed to save graph settings:', e)
  }
}

// Debounced save for viewport changes
const debouncedSaveSettings = useDebounceFn(saveGraphSettings, 500)

// ============================================
// GRAPH STATE
// ============================================
const handlePositions: Record<string, VueFlowPosition> = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right
}

const nodes = ref<Node[]>([])
const edges = ref<Edge[]>([])

type ConnectionMode = 'all' | 'active' | 'none'
// Load initial connectionMode from localStorage
const savedSettings = loadGraphSettings()
const connectionMode = ref<ConnectionMode>(savedSettings.connectionMode)

const cycleConnectionMode = () => {
  const modes: ConnectionMode[] = ['all', 'active', 'none']
  const currentIndex = modes.indexOf(connectionMode.value)
  connectionMode.value = modes[(currentIndex + 1) % modes.length]
  saveGraphSettings() // Persist the change
  buildGraph()
}

// ============================================
// DIALOG STATE
// ============================================
const dialogs = useDialogs({
  connectConfirm: {},
  disconnectConfirm: {},
  editScene: {},
  deleteScene: {},
  addSpotify: {},
  editSpotify: {},
  deleteSpotify: {},
  addMusic: {},
  editMusic: {},
  deleteMusic: {},
  addPlaylist: {},
  deletePlaylist: {},
  deleteContainer: {}
})

// Connection state
const pendingConnection = ref<Connection | null>(null)
const isConnecting = ref(false)
const sceneToDisconnect = ref<string | null>(null)
const isDisconnecting = ref(false)

// Scene state
const sceneToEdit = ref<Scene | null>(null)
const sceneToDelete = ref<Scene | null>(null)
const editSceneName = ref('')
const editSceneDescription = ref('')
const isEditingScene = ref(false)
const isDeletingScene = ref(false)

// Spotify state
const spotifyUrlInput = ref('')
const spotifyNodeToEdit = ref<SpotifyNode | null>(null)
const spotifyNodeToDelete = ref<SpotifyNode | null>(null)
const isCreatingSpotify = ref(false)
const isDeletingSpotify = ref(false)
const editSpotifyName = ref('')
const editSpotifyVolume = ref([100])
const spotifyArtworkCache = ref<Map<string, string | null>>(new Map())

// Music state
const musicFileInput = ref<HTMLInputElement | null>(null)
const musicNodeToEdit = ref<MusicNode | null>(null)
const musicNodeToDelete = ref<MusicNode | null>(null)
const isCreatingMusic = ref(false)
const isDeletingMusic = ref(false)
const editMusicName = ref('')
const editMusicVolume = ref([100])

// Playlist state
const playlistNameInput = ref('')
const playlistNodeToDelete = ref<PlaylistNode | null>(null)
const isCreatingPlaylist = ref(false)
const isDeletingPlaylist = ref(false)
// Audio-Scene links
interface AudioSceneLink {
  id: string
  scene_id: string
  audio_node_id: string
  type: 'spotify' | 'music' | 'playlist'
}
const audioSceneLinks = ref<AudioSceneLink[]>([])

// Helper to get linked music node IDs for a scene
const getLinkedMusicNodeIds = (sceneId: string): string[] => {
  return audioSceneLinks.value
    .filter(link => link.scene_id === sceneId && link.type === 'music')
    .map(link => link.audio_node_id)
}

// Helper to get linked playlist node IDs for a scene
const getLinkedPlaylistNodeIds = (sceneId: string): string[] => {
  return audioSceneLinks.value
    .filter(link => link.scene_id === sceneId && link.type === 'playlist')
    .map(link => link.audio_node_id)
}

// All scenes as array for SceneNode props
const allScenesArray = computed(() => Array.from(scenesStore.scenes.values()))

// Context menu
const contextMenu = ref({
  isOpen: false,
  x: 0,
  y: 0,
  canvasX: 0,
  canvasY: 0
})

// Long-press state for mobile context menu
const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const longPressStart = ref<{ x: number; y: number } | null>(null)
const LONG_PRESS_DURATION = 500 // milliseconds
const LONG_PRESS_THRESHOLD = 10 // pixels - cancel if moved more than this

// Drag and drop state
const isDraggingOver = ref(false)
const dragType = ref<'audio' | 'spotify' | null>(null)

// ============================================
// COMPUTED
// ============================================
const childSceneName = computed(() => {
  if (!pendingConnection.value?.target) return ''
  return scenesStore.scenes.get(pendingConnection.value.target)?.name || ''
})

const parentSceneName = computed(() => {
  if (!pendingConnection.value?.source) return ''
  return scenesStore.scenes.get(pendingConnection.value.source)?.name || ''
})

const descendantCount = computed(() => {
  if (!pendingConnection.value?.target) return 0
  return scenesStore.getDescendants(pendingConnection.value.target).length
})

const sceneToDisconnectName = computed(() => {
  if (!sceneToDisconnect.value) return ''
  return scenesStore.scenes.get(sceneToDisconnect.value)?.name || ''
})

const descendantCountToDisconnect = computed(() => {
  if (!sceneToDisconnect.value) return 0
  return scenesStore.getDescendants(sceneToDisconnect.value).length
})

const defaultPlaybackTarget = computed(() => {
  const displays = displaysStore.allDisplays
  return displays.length > 0 ? displays[0].id : ''
})

// ============================================
// PLAYLIST LAYOUT CONSTANTS
// ============================================
const PLAYLIST_FIXED_WIDTH = 240
const PLAYLIST_HEADER_HEIGHT = 60
const PLAYLIST_NODE_HEIGHT = 64  // Matches MusicNode component height
const PLAYLIST_NODE_GAP = 10
const PLAYLIST_BOTTOM_PADDING = 20
const MUSIC_NODE_WIDTH = 200
// Center music nodes: (playlist width - node width) / 2
const PLAYLIST_LEFT_PADDING = (PLAYLIST_FIXED_WIDTH - MUSIC_NODE_WIDTH) / 2

const getPlaylistCalculatedHeight = (playlistId: string): number => {
  const trackCount = musicNodesStore.getMusicNodesByPlaylistId(playlistId).length
  // Height = header + (tracks * (height + gap)) - last gap + bottom padding
  const contentHeight = trackCount > 0 
    ? (trackCount * PLAYLIST_NODE_HEIGHT) + ((trackCount - 1) * PLAYLIST_NODE_GAP)
    : 0
  const autoHeight = PLAYLIST_HEADER_HEIGHT + contentHeight + PLAYLIST_BOTTOM_PADDING
  return Math.max(150, autoHeight)
}

// ============================================
// GRAPH BUILDING
// ============================================
let buildGraphTimeout: ReturnType<typeof setTimeout> | null = null
let buildGraphPending = false

// Debounced buildGraph to prevent multiple rapid calls during initialization
const debouncedBuildGraph = (immediate = false) => {
  if (immediate) {
    if (buildGraphTimeout) clearTimeout(buildGraphTimeout)
    buildGraphTimeout = null
    buildGraphPending = false
    buildGraphInternal()
    return
  }
  
  buildGraphPending = true
  if (buildGraphTimeout) return // Already scheduled
  
  buildGraphTimeout = setTimeout(() => {
    buildGraphTimeout = null
    if (buildGraphPending) {
      buildGraphPending = false
      buildGraphInternal()
    }
  }, 50) // 50ms debounce
}

// Alias for backward compatibility - most calls should use debounced version
const buildGraph = () => debouncedBuildGraph(false)

const buildGraphInternal = () => {
  console.time('[SceneGraphView] buildGraphInternal')
  const sceneArray = Array.from(scenesStore.scenes.values()).filter(scene => scene && scene.id)
  const spotifyArray = spotifyNodesStore.allSpotifyNodes
  const musicArray = musicNodesStore.allMusicNodes
  const playlistArray = playlistNodesStore.allPlaylistNodes
  const containerArray = graphContainersStore.allContainers
  
  console.log('[SceneGraphView] Building graph with:', {
    scenes: sceneArray.length,
    spotify: spotifyArray.length,
    music: musicArray.length,
    playlists: playlistArray.length,
    containers: containerArray.length
  })
  const previewDisplay = displaysStore.activeDisplays[0] || null

  // Create lookup maps for collapsed states
  const collapsedContainers = new Set(containerArray.filter(c => c.is_collapsed).map(c => c.id))
  const collapsedPlaylists = new Set(playlistArray.filter(p => p.is_collapsed).map(p => p.id))

  // Container nodes (rendered first, at the back)
  const containerNodes: Node[] = containerArray.map(container => ({
    id: `container-${container.id}`,
    type: 'container',
    position: { x: container.position_x, y: container.position_y },
    style: { width: `${container.width}px`, height: `${container.is_collapsed ? 50 : container.height}px` },
    draggable: true,
    selectable: true,
    data: {
      id: container.id,
      name: container.name,
      color: container.color,
      background_image_path: container.background_image_path,
      width: container.width,
      height: container.height,
      is_collapsed: container.is_collapsed
    },
    zIndex: -1 // Render behind other nodes
  }))

  // Playlist nodes (container-like, rendered at the back like containers)
  // Uses fixed width and auto-calculated height
  const playlistNodes: Node[] = playlistArray.map(node => {
    // Use shared height calculation
    const autoHeight = getPlaylistCalculatedHeight(node.id)
    
    return {
      id: `playlist-${node.id}`,
      type: 'playlist',
      position: { x: node.position_x, y: node.position_y },
      style: { width: `${PLAYLIST_FIXED_WIDTH}px`, height: `${node.is_collapsed ? 50 : autoHeight}px` },
      parentNode: node.container_id ? `container-${node.container_id}` : undefined,
      draggable: true,
      selectable: true,
      expandParent: false,
      hidden: node.container_id ? collapsedContainers.has(node.container_id) : false,
      data: { 
        id: node.id,
        name: node.name,
        shuffle: node.shuffle,
        repeat_mode: node.repeat_mode,
        volume: node.volume,
        playback_target: node.playback_target,
        width: PLAYLIST_FIXED_WIDTH,
        height: autoHeight, // Use auto-calculated height
        is_collapsed: node.is_collapsed
      },
      zIndex: -1 // Render behind regular nodes but can contain music nodes
    }
  })

  // Scene nodes - hidden if parent container is collapsed
  const sceneNodes: Node[] = sceneArray.map((scene, index) => ({
    id: scene.id,
    type: 'scene',
    position: calculatePosition(scene, index, sceneArray),
    parentNode: scene.container_id ? `container-${scene.container_id}` : undefined,
    expandParent: false,
    hidden: scene.container_id ? collapsedContainers.has(scene.container_id) : false,
    data: { 
      scene,
      preview: previewDisplay?.id ? { display: previewDisplay } : null
    }
  }))

  // Spotify nodes - hidden if parent container is collapsed
  const spotifyNodes: Node[] = spotifyArray.map(node => ({
    id: `spotify-${node.id}`,
    type: 'spotify',
    position: { x: node.position_x, y: node.position_y },
    parentNode: node.container_id ? `container-${node.container_id}` : undefined,
    expandParent: false,
    hidden: node.container_id ? collapsedContainers.has(node.container_id) : false,
    data: { node }
  }))

  // Music nodes - can be inside containers OR playlists
  // Hidden if parent container or playlist is collapsed
  // When inside a playlist, position is calculated based on playlist_order for vertical layout
  const musicNodes: Node[] = musicArray.map(node => {
    // Determine parent: playlist takes precedence, then container
    let parentNode: string | undefined = undefined
    let isHidden = false
    let position = { x: node.position_x, y: node.position_y }
    
    if (node.playlist_id) {
      parentNode = `playlist-${node.playlist_id}`
      // Hidden if playlist is collapsed OR if playlist's container is collapsed
      const playlist = playlistArray.find(p => p.id === node.playlist_id)
      isHidden = collapsedPlaylists.has(node.playlist_id) || 
                 (playlist?.container_id ? collapsedContainers.has(playlist.container_id) : false)
      
      // Calculate vertical position based on playlist_order using shared constants
      position = {
        x: PLAYLIST_LEFT_PADDING,
        y: PLAYLIST_HEADER_HEIGHT + (node.playlist_order * (PLAYLIST_NODE_HEIGHT + PLAYLIST_NODE_GAP))
      }
    } else if (node.container_id) {
      parentNode = `container-${node.container_id}`
      isHidden = collapsedContainers.has(node.container_id)
    }
    
    return {
      id: `music-${node.id}`,
      type: 'music',
      position,
      parentNode,
      expandParent: false,
      hidden: isHidden,
      data: { node }
    }
  })

  nodes.value = [...containerNodes, ...playlistNodes, ...sceneNodes, ...spotifyNodes, ...musicNodes]

  // Build edges
  const getAncestors = (sceneId: string): string[] => {
    const scene = scenesStore.scenes.get(sceneId)
    if (!scene?.parent_id) return []
    return [scene.parent_id, ...getAncestors(scene.parent_id)]
  }

  const activeScene = sceneArray.find(s => s.is_active)
  const activeAncestors = activeScene ? new Set([activeScene.id, ...getAncestors(activeScene.id)]) : new Set()

  const sceneEdges = sceneArray
    .filter(scene => scene.parent_id)
    .map(scene => {
      const isActiveEdge = activeAncestors.has(scene.id) && activeAncestors.has(scene.parent_id!)
      return {
        id: `edge-${scene.parent_id}-${scene.id}`,
        source: scene.parent_id!,
        target: scene.id,
        type: 'step',
        animated: isActiveEdge,
        style: { stroke: isActiveEdge ? 'hsl(var(--primary))' : 'hsl(var(--border))' },
        label: scene.is_active ? '×' : undefined,
        labelBgPadding: scene.is_active ? [6, 6] as [number, number] : undefined,
        labelBgBorderRadius: scene.is_active ? 10 : undefined,
        data: { sceneId: scene.id, onDisconnect: () => startDisconnect(scene.id) },
        hidden: connectionMode.value === 'none' || (connectionMode.value === 'active' && !isActiveEdge)
      }
    })

  const getAudioEdgeColor = (type: string) => {
    switch (type) {
      case 'spotify': return '#1DB954'
      case 'playlist': return 'var(--gold-solid)'  // Gold for playlists
      default: return 'hsl(var(--primary))'
    }
  }

  const audioEdges = audioSceneLinks.value.map(link => ({
    id: `audio-edge-${link.id}`,
    source: `${link.type}-${link.audio_node_id}`,
    sourceHandle: 'audio-out',
    target: link.scene_id,
    targetHandle: 'audio-in',
    type: 'step',
    animated: false,
    style: { stroke: getAudioEdgeColor(link.type), strokeDasharray: '5,5' },
    label: '×',
    labelBgPadding: [4, 4] as [number, number],
    labelBgBorderRadius: 8,
    labelStyle: { fontSize: '14px', fontWeight: '500' },
    data: { audioLinkId: link.id, type: link.type, audioNodeId: link.audio_node_id, sceneId: link.scene_id }
  }))

  edges.value = [...sceneEdges, ...audioEdges]
  
  console.log('[SceneGraphView] Graph built:', {
    nodes: nodes.value.length,
    edges: edges.value.length
  })
  console.timeEnd('[SceneGraphView] buildGraphInternal')
}

const calculatePosition = (scene: Scene, index: number, allScenes: Scene[]) => {
  if (scene.graph_position_x !== null && scene.graph_position_y !== null) {
    return { x: scene.graph_position_x, y: scene.graph_position_y }
  }
  return { x: 100 + (index * 200), y: 100 + (scene.hierarchy_level * 150) }
}

// ============================================
// EVENT HANDLERS
// ============================================
const onNodesChange = async (changes: Array<any>) => {
  // This handler is mostly redundant since onNodeDragStop handles all cases
  // But we keep it for any edge cases we might miss
  for (const change of changes) {
    if (change.type === 'position' && change.position && change.dragging === false) {
      const id = change.id
      // Only handle scene nodes here, other types are handled in onNodeDragStop
      if (!id.startsWith('container-') && !id.startsWith('playlist-') && 
          !id.startsWith('spotify-') && !id.startsWith('music-')) {
        // This is a scene node - update is already handled in onNodeDragStop
        // so we don't need to do anything here
      }
    }
  }
}

const onEdgesChange = () => {}

// Simple drag handler - no preview animation for now
const onNodeDrag = () => {}

// Helper to find which container a position is inside of
const findContainerAtPosition = (x: number, y: number, excludeNodeId?: string): GraphContainer | null => {
  const containers = graphContainersStore.allContainers
  for (const container of containers) {
    if (excludeNodeId === `container-${container.id}`) continue
    if (
      x >= container.position_x &&
      x <= container.position_x + container.width &&
      y >= container.position_y &&
      y <= container.position_y + container.height
    ) {
      return container
    }
  }
  return null
}

// Helper to find which playlist a position is inside of (for music nodes)
const findPlaylistAtPosition = (x: number, y: number, excludeNodeId?: string): PlaylistNode | null => {
  const playlists = playlistNodesStore.allPlaylistNodes
  const containers = graphContainersStore.allContainers
  
  for (const playlist of playlists) {
    if (excludeNodeId === `playlist-${playlist.id}`) continue
    if (playlist.is_collapsed) continue // Can't drop into collapsed playlists
    
    // Calculate absolute position (account for container if playlist is inside one)
    let absoluteX = playlist.position_x
    let absoluteY = playlist.position_y
    
    if (playlist.container_id) {
      const parentContainer = containers.find(c => c.id === playlist.container_id)
      if (parentContainer) {
        absoluteX += parentContainer.position_x
        absoluteY += parentContainer.position_y
      }
    }
    
    const calculatedHeight = getPlaylistCalculatedHeight(playlist.id)
    if (
      x >= absoluteX &&
      x <= absoluteX + PLAYLIST_FIXED_WIDTH &&
      y >= absoluteY &&
      y <= absoluteY + calculatedHeight
    ) {
      return playlist
    }
  }
  return null
}

// Get absolute position of a playlist (accounting for container parent)
const getPlaylistAbsolutePosition = (playlist: PlaylistNode): { x: number, y: number } => {
  let x = playlist.position_x
  let y = playlist.position_y
  
  if (playlist.container_id) {
    const containers = graphContainersStore.allContainers
    const parentContainer = containers.find(c => c.id === playlist.container_id)
    if (parentContainer) {
      x += parentContainer.position_x
      y += parentContainer.position_y
    }
  }
  
  return { x, y }
}

// Calculate new order for a music node based on drop Y position
const calculateNewOrderInPlaylist = (playlistId: string, dropY: number, draggedNodeId: string): number => {
  const existingNodes = musicNodesStore.getMusicNodesByPlaylistId(playlistId)
    .filter(n => n.id !== draggedNodeId)
  
  if (existingNodes.length === 0) return 0
  
  // Calculate slot from Y position relative to header
  const relativeY = dropY - PLAYLIST_HEADER_HEIGHT
  const slot = Math.floor(relativeY / (PLAYLIST_NODE_HEIGHT + PLAYLIST_NODE_GAP))
  
  return Math.max(0, Math.min(slot, existingNodes.length))
}

const onNodeDragStop = async (event: any) => {
  const nodesToProcess = event.nodes || (event.node ? [event.node] : [])
  if (nodesToProcess.length === 0) return
  
  for (const node of nodesToProcess) {
    if (!node?.id || !node?.position) continue
    
    const { id, position } = node
    // computedPosition gives absolute coordinates even when node has a parent
    const absolutePosition = node.computedPosition || position
    
    // Handle container nodes
    if (id.startsWith('container-')) {
      await graphContainersStore.updateContainer(id.replace('container-', ''), { 
        position_x: position.x, 
        position_y: position.y 
      })
      continue
    }
    
    // Handle playlist nodes
    if (id.startsWith('playlist-')) {
      const container = findContainerAtPosition(absolutePosition.x, absolutePosition.y, id)
      const currentParent = node.parentNode
      const wasInContainer = currentParent?.startsWith('container-')
      
      // Check if container membership is changing
      const currentContainerId = wasInContainer ? currentParent.replace('container-', '') : null
      const newContainerId = container?.id || null
      
      let savePosition = position
      if (container && !wasInContainer) {
        // Entering container - convert to relative position
        savePosition = {
          x: absolutePosition.x - container.position_x,
          y: absolutePosition.y - container.position_y
        }
      } else if (!container && wasInContainer) {
        // Leaving container - use absolute position
        savePosition = absolutePosition
      }
      
      await playlistNodesStore.updatePlaylistNode(id.replace('playlist-', ''), { 
        position_x: savePosition.x, 
        position_y: savePosition.y,
        container_id: newContainerId
      })
      
      // Rebuild graph if container membership changed
      if (currentContainerId !== newContainerId) {
        buildGraph()
      }
      continue
    }
    
    // Handle spotify nodes
    if (id.startsWith('spotify-')) {
      const container = findContainerAtPosition(absolutePosition.x, absolutePosition.y, id)
      const currentParent = node.parentNode
      const wasInContainer = currentParent?.startsWith('container-')
      
      // Check if container membership is changing
      const currentContainerId = wasInContainer ? currentParent.replace('container-', '') : null
      const newContainerId = container?.id || null
      
      let savePosition = position
      if (container && !wasInContainer) {
        // Entering container - convert to relative
        savePosition = {
          x: absolutePosition.x - container.position_x,
          y: absolutePosition.y - container.position_y
        }
      } else if (!container && wasInContainer) {
        // Leaving container - use absolute
        savePosition = absolutePosition
      }
      
      await spotifyNodesStore.updateSpotifyNode(id.replace('spotify-', ''), { 
        position_x: savePosition.x, 
        position_y: savePosition.y,
        container_id: newContainerId
      })
      
      // Rebuild graph if container membership changed
      if (currentContainerId !== newContainerId) {
        buildGraph()
      }
      continue
    }
    
    // Handle music nodes - the complex case
    if (id.startsWith('music-')) {
      const nodeId = id.replace('music-', '')
      const currentNode = musicNodesStore.getMusicNodeById(nodeId)
      if (!currentNode) continue
      
      const currentParent = node.parentNode
      const wasInPlaylist = currentParent?.startsWith('playlist-')
      const currentPlaylistId = wasInPlaylist ? currentParent.replace('playlist-', '') : null
      
      // Check what we're dropping into
      const targetPlaylist = findPlaylistAtPosition(absolutePosition.x, absolutePosition.y, id)
      const targetContainer = findContainerAtPosition(absolutePosition.x, absolutePosition.y, id)
      
      // Case 1: Moving within the same playlist
      if (targetPlaylist && currentPlaylistId === targetPlaylist.id) {
        // Calculate new order from drop position (use absolute playlist position)
        const playlistAbsPos = getPlaylistAbsolutePosition(targetPlaylist)
        const dropY = absolutePosition.y - playlistAbsPos.y
        const newOrder = calculateNewOrderInPlaylist(targetPlaylist.id, dropY, nodeId)
        
        // Get all nodes in playlist and reorder
        const allNodes = musicNodesStore.getMusicNodesByPlaylistId(targetPlaylist.id)
        const currentOrder = currentNode.playlist_order
        
        if (newOrder !== currentOrder) {
          // Reorder: remove from old position, insert at new
          for (const n of allNodes) {
            if (n.id === nodeId) continue
            
            let newNodeOrder = n.playlist_order
            if (currentOrder < newOrder) {
              // Moving down: shift nodes between old and new up
              if (n.playlist_order > currentOrder && n.playlist_order <= newOrder) {
                newNodeOrder = n.playlist_order - 1
              }
            } else {
              // Moving up: shift nodes between new and old down
              if (n.playlist_order >= newOrder && n.playlist_order < currentOrder) {
                newNodeOrder = n.playlist_order + 1
              }
            }
            
            if (newNodeOrder !== n.playlist_order) {
              await musicNodesStore.updateMusicNode(n.id, { playlist_order: newNodeOrder })
            }
          }
          
          await musicNodesStore.updateMusicNode(nodeId, { playlist_order: newOrder })
        }
        
        buildGraph()
        continue
      }
      
      // Case 2: Moving into a different playlist
      if (targetPlaylist && currentPlaylistId !== targetPlaylist.id) {
        // Close gap in old playlist if coming from one
        if (currentPlaylistId) {
          const oldPlaylistNodes = musicNodesStore.getMusicNodesByPlaylistId(currentPlaylistId)
          for (const n of oldPlaylistNodes) {
            if (n.id !== nodeId && n.playlist_order > currentNode.playlist_order) {
              await musicNodesStore.updateMusicNode(n.id, { 
                playlist_order: n.playlist_order - 1 
              })
            }
          }
        }
        
        // Calculate insert position in new playlist (use absolute playlist position)
        const playlistAbsPos = getPlaylistAbsolutePosition(targetPlaylist)
        const dropY = absolutePosition.y - playlistAbsPos.y
        const insertOrder = calculateNewOrderInPlaylist(targetPlaylist.id, dropY, nodeId)
        
        // Shift nodes in target playlist to make room
        const targetNodes = musicNodesStore.getMusicNodesByPlaylistId(targetPlaylist.id)
        for (const n of targetNodes) {
          if (n.playlist_order >= insertOrder) {
            await musicNodesStore.updateMusicNode(n.id, { 
              playlist_order: n.playlist_order + 1 
            })
          }
        }
        
        // Move the node
        await musicNodesStore.updateMusicNode(nodeId, {
          playlist_id: targetPlaylist.id,
          container_id: null,
          playlist_order: insertOrder
        })
        
        buildGraph()
        continue
      }
      
      // Case 3: Moving out of a playlist (to free space or container)
      if (!targetPlaylist && currentPlaylistId) {
        // Close gap in old playlist
        const oldPlaylistNodes = musicNodesStore.getMusicNodesByPlaylistId(currentPlaylistId)
        for (const n of oldPlaylistNodes) {
          if (n.id !== nodeId && n.playlist_order > currentNode.playlist_order) {
            await musicNodesStore.updateMusicNode(n.id, { 
              playlist_order: n.playlist_order - 1 
            })
          }
        }
        
        // Update node position to absolute
        await musicNodesStore.updateMusicNode(nodeId, {
          playlist_id: null,
          container_id: targetContainer?.id || null,
          playlist_order: 0,
          position_x: absolutePosition.x - (targetContainer?.position_x || 0),
          position_y: absolutePosition.y - (targetContainer?.position_y || 0)
        })
        
        buildGraph()
        continue
      }
      
      // Case 4: Moving freely (not in playlist, not entering playlist)
      let savePosition = position
      const wasInContainer = currentParent?.startsWith('container-')
      const currentContainerId = wasInContainer ? currentParent.replace('container-', '') : null
      const newContainerId = targetContainer?.id || null
      
      if (targetContainer && !wasInContainer) {
        savePosition = {
          x: absolutePosition.x - targetContainer.position_x,
          y: absolutePosition.y - targetContainer.position_y
        }
      } else if (!targetContainer && wasInContainer) {
        savePosition = absolutePosition
      }
      
      await musicNodesStore.updateMusicNode(nodeId, { 
        position_x: savePosition.x, 
        position_y: savePosition.y,
        container_id: newContainerId
      })
      
      // Rebuild graph if container membership changed
      if (currentContainerId !== newContainerId) {
        buildGraph()
      }
    }
    
    // Handle scene nodes
    if (!id.startsWith('spotify-') && !id.startsWith('music-') && !id.startsWith('playlist-') && !id.startsWith('container-')) {
      const container = findContainerAtPosition(absolutePosition.x, absolutePosition.y, id)
      const currentParent = node.parentNode
      const wasInContainer = currentParent?.startsWith('container-')
      
      if (container?.id !== (wasInContainer ? currentParent.replace('container-', '') : null)) {
        let savePosition = position
        if (container && !wasInContainer) {
          savePosition = {
            x: absolutePosition.x - container.position_x,
            y: absolutePosition.y - container.position_y
          }
        } else if (!container && wasInContainer) {
          savePosition = absolutePosition
        }
        await scenesStore.updateSceneContainer(id, container?.id || null, savePosition)
        buildGraph() // Rebuild graph to update parent-child relationships
      } else {
        await scenesStore.updateScenePosition(id, position.x, position.y)
      }
    }
  }
}

const onEdgeClick = async (event: any) => {
  const edge = event.edge
  if (!edge) return

  const originalEvent = event.event as MouseEvent
  const target = originalEvent?.target as HTMLElement | SVGElement
  const isLabelClick = target?.closest('.vue-flow__edge-textbg') || target?.closest('.vue-flow__edge-text') || target?.tagName === 'text' || target?.tagName === 'rect'
  if (!isLabelClick) return

  if (edge.data?.audioLinkId) {
    const { type, audioNodeId, sceneId } = edge.data
    if (type === 'spotify') await unlinkSpotifyFromScene(audioNodeId, sceneId)
    else if (type === 'music') await unlinkMusicFromScene(audioNodeId, sceneId)
    else if (type === 'playlist') await unlinkPlaylistFromScene(audioNodeId, sceneId)
    return
  }

  if (edge.data?.sceneId) startDisconnect(edge.data.sceneId)
}

const onConnect = async (connection: Connection) => {
  if (!connection.source || !connection.target) return

  const sourceIsSpotify = connection.source.startsWith('spotify-')
  const sourceIsMusic = connection.source.startsWith('music-')
  const sourceIsPlaylist = connection.source.startsWith('playlist-')
  const targetIsScene = !connection.target.startsWith('spotify-') && 
                        !connection.target.startsWith('music-') && 
                        !connection.target.startsWith('playlist-') &&
                        !connection.target.startsWith('container-')

  if ((sourceIsSpotify || sourceIsMusic || sourceIsPlaylist) && targetIsScene) {
    if (sourceIsSpotify) await linkSpotifyToScene(connection.source.replace('spotify-', ''), connection.target)
    else if (sourceIsMusic) await linkMusicToScene(connection.source.replace('music-', ''), connection.target)
    else if (sourceIsPlaylist) await linkPlaylistToScene(connection.source.replace('playlist-', ''), connection.target)
    buildGraph()
    return
  }

  if (scenesStore.detectCycle(connection.target, connection.source)) return
  pendingConnection.value = connection
  dialogs.connectConfirm.open()
}

const onCanvasRightClick = async (event: any) => {
  event.event?.preventDefault()
  const mouseEvent = event.event || event
  const flowCoordinates = project({ x: mouseEvent.clientX, y: mouseEvent.clientY })
  contextMenu.value = {
    isOpen: true,
    x: mouseEvent.clientX,
    y: mouseEvent.clientY,
    canvasX: flowCoordinates.x,
    canvasY: flowCoordinates.y
  }
}

// Long-press handlers for mobile context menu
const onPaneTouchStart = (event: TouchEvent) => {
  if (event.touches.length !== 1) return // Only single touch
  
  const touch = event.touches[0]
  longPressStart.value = { x: touch.clientX, y: touch.clientY }
  
  longPressTimer.value = setTimeout(() => {
    if (longPressStart.value) {
      // Trigger context menu at the touch position
      const flowCoordinates = project({ x: longPressStart.value.x, y: longPressStart.value.y })
      contextMenu.value = {
        isOpen: true,
        x: longPressStart.value.x,
        y: longPressStart.value.y,
        canvasX: flowCoordinates.x,
        canvasY: flowCoordinates.y
      }
      // Vibrate for haptic feedback if supported
      if (navigator.vibrate) {
        navigator.vibrate(50)
      }
    }
    longPressTimer.value = null
    longPressStart.value = null
  }, LONG_PRESS_DURATION)
}

const onPaneTouchMove = (event: TouchEvent) => {
  if (!longPressStart.value || !longPressTimer.value) return
  
  const touch = event.touches[0]
  const dx = touch.clientX - longPressStart.value.x
  const dy = touch.clientY - longPressStart.value.y
  const distance = Math.sqrt(dx * dx + dy * dy)
  
  // Cancel long press if moved too much (user is panning)
  if (distance > LONG_PRESS_THRESHOLD) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
    longPressStart.value = null
  }
}

const onPaneTouchEnd = () => {
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
  longPressStart.value = null
}

// ============================================
// SCENE ACTIONS
// ============================================
const activateScene = async (sceneId: string) => {
  await scenesStore.activateScene(sceneId)
  await router.push({ name: 'scene', params: { sceneId } })
  buildGraph()
  
  // Play linked Spotify content for this scene
  await playLinkedSpotifyForScene(sceneId)
  // Play linked playlists for this scene
  await playLinkedPlaylistsForScene(sceneId)
}

// Play Spotify content linked to a scene
const playLinkedSpotifyForScene = async (sceneId: string) => {
  // Find Spotify links for this scene
  const spotifyLinks = audioSceneLinks.value.filter(
    link => link.scene_id === sceneId && link.type === 'spotify'
  )
  
  if (spotifyLinks.length === 0) return
  
  // Get the first linked Spotify node and play it
  const firstLink = spotifyLinks[0]
  const spotifyNode = spotifyNodesStore.getSpotifyNodeById(firstLink.audio_node_id)
  
  if (!spotifyNode?.spotify_url) return
  
  const urlInfo = parseSpotifyUrl(spotifyNode.spotify_url)
  if (!urlInfo) return
  
  try {
    const spotifyUri = `spotify:${urlInfo.type}:${urlInfo.id}`
    
    if (urlInfo.type === 'track') {
      await spotifyStore.play({ uris: [spotifyUri] })
    } else if (['playlist', 'album', 'artist'].includes(urlInfo.type)) {
      await spotifyStore.play({ context_uri: spotifyUri })
    }
    
    logger.info(`▶️ Auto-playing Spotify for scene: ${spotifyNode.name}`)
  } catch (error) {
    logger.warn('Failed to auto-play Spotify for scene:', error)
  }
}

// Play linked playlists for a scene
const playLinkedPlaylistsForScene = async (sceneId: string) => {
  // Find playlist links for this scene
  const playlistLinks = audioSceneLinks.value.filter(
    link => link.scene_id === sceneId && link.type === 'playlist'
  )
  
  if (playlistLinks.length === 0) return
  
  // Play each linked playlist
  for (const link of playlistLinks) {
    const playlist = playlistNodesStore.getPlaylistNodeById(link.audio_node_id)
    if (!playlist) continue
    
    // Get music nodes in this playlist
    const musicNodes = musicNodesStore.getMusicNodesByPlaylistId(playlist.id)
    if (musicNodes.length === 0) continue
    
    // Find the target display for playback
    const firstDisplay = displaysStore.allDisplays[0]
    const targetDisplayId = playlist.playback_target || firstDisplay?.id
    if (!targetDisplayId) continue
    
    // Start playing the first track (or random if shuffle is on)
    const trackIndex = playlist.shuffle ? Math.floor(Math.random() * musicNodes.length) : 0
    const firstTrack = musicNodes[trackIndex]
    
    if (firstTrack) {
      await togglePlaylistPlayback(playlist.id)
      logger.info(`▶️ Auto-playing playlist for scene: ${playlist.name}`)
    }
  }
}

const createSceneAtPosition = async () => {
  contextMenu.value.isOpen = false
  const result = await scenesStore.createScene({
    name: generateSceneName(),
    parent_id: null,
    graph_position_x: contextMenu.value.canvasX,
    graph_position_y: contextMenu.value.canvasY
  })
  if (result.success) buildGraph()
}

const createChildScene = async (parentSceneId: string) => {
  const parentScene = scenesStore.scenes.get(parentSceneId)
  if (!parentScene) return
  
  const parentNode = nodes.value.find(n => n.id === parentSceneId)
  const posX = parentNode ? parentNode.position.x : (parentScene.graph_position_x || 0)
  const posY = parentNode ? parentNode.position.y - 200 : (parentScene.graph_position_y || 0) - 200
  
  const result = await scenesStore.createScene({
    name: generateSceneName(),
    parent_id: parentScene.id,
    graph_position_x: posX,
    graph_position_y: posY
  })
  if (result.success) buildGraph()
}

const generateSceneName = () => {
  const existingNames = Array.from(scenesStore.scenes.values()).map(s => s.name)
  let counter = 1
  let name = 'New Scene'
  while (existingNames.includes(name)) {
    counter++
    name = `New Scene ${counter}`
  }
  return name
}

const openEditSceneDialog = (scene: Scene) => {
  sceneToEdit.value = scene
  editSceneName.value = scene.name
  editSceneDescription.value = scene.description || ''
  dialogs.editScene.open()
}

const confirmEditScene = async () => {
  if (!sceneToEdit.value || !editSceneName.value.trim()) return
  isEditingScene.value = true
  try {
    await scenesStore.updateSceneName(sceneToEdit.value.id, editSceneName.value.trim())
    await scenesStore.updateSceneDescription(sceneToEdit.value.id, editSceneDescription.value.trim() || '')
    buildGraph()
    dialogs.editScene.close()
    sceneToEdit.value = null
  } finally {
    isEditingScene.value = false
  }
}

const confirmDeleteScene = (scene: Scene) => {
  sceneToDelete.value = scene
  dialogs.deleteScene.open()
}

const executeDeleteScene = async () => {
  if (!sceneToDelete.value) return
  isDeletingScene.value = true
  try {
    const result = await scenesStore.deleteScene(sceneToDelete.value.id)
    if (result.success) {
      buildGraph()
      dialogs.deleteScene.close()
      sceneToDelete.value = null
    }
  } finally {
    isDeletingScene.value = false
  }
}

const duplicateScene = async (scene: Scene) => {
  const sceneNode = nodes.value.find(n => n.id === scene.id)
  const posX = sceneNode ? sceneNode.position.x + 50 : (scene.graph_position_x || 0) + 50
  const posY = sceneNode ? sceneNode.position.y + 50 : (scene.graph_position_y || 0) + 50
  
  const result = await scenesStore.createScene({
    name: `${scene.name} (Copy)`,
    description: scene.description || undefined,
    parent_id: scene.parent_id,
    graph_position_x: posX,
    graph_position_y: posY
  })
  if (result.success) buildGraph()
}

// Set parent for a scene via dropdown menu search
const setSceneParent = async (sceneId: string, parentId: string | null) => {
  // Check for cycles if setting a parent
  if (parentId && scenesStore.detectCycle(sceneId, parentId)) {
    toast({
      title: 'Cannot set parent',
      description: 'This would create a circular hierarchy',
      variant: 'destructive'
    })
    return
  }
  
  const result = await scenesStore.updateSceneParent(sceneId, parentId)
  if (result.success) {
    buildGraph()
    toast({
      title: parentId ? 'Parent set' : 'Parent removed',
      description: parentId 
        ? `Scene parent has been updated`
        : 'Scene is now a root scene'
    })
  }
}

// Link music node to scene via dropdown menu search
const linkMusicToSceneFromMenu = async (sceneId: string, musicNodeId: string) => {
  await linkMusicToScene(musicNodeId, sceneId)
  buildGraph()
  toast({
    title: 'Music linked',
    description: 'Music node has been linked to the scene'
  })
}

// Link playlist to scene via dropdown menu search
const linkPlaylistToSceneFromMenu = async (sceneId: string, playlistNodeId: string) => {
  await linkPlaylistToScene(playlistNodeId, sceneId)
  buildGraph()
  toast({
    title: 'Playlist linked',
    description: 'Playlist has been linked to the scene'
  })
}

// ============================================
// CONNECTION ACTIONS
// ============================================
const confirmConnect = async () => {
  if (!pendingConnection.value?.source || !pendingConnection.value?.target) return
  isConnecting.value = true
  try {
    const result = await scenesStore.updateSceneParent(pendingConnection.value.target, pendingConnection.value.source)
    if (result.success) {
      buildGraph()
      dialogs.connectConfirm.close()
    }
  } finally {
    isConnecting.value = false
    pendingConnection.value = null
  }
}

const startDisconnect = (sceneId: string) => {
  sceneToDisconnect.value = sceneId
  dialogs.disconnectConfirm.open()
}

const confirmDisconnect = async () => {
  if (!sceneToDisconnect.value) return
  isDisconnecting.value = true
  try {
    const result = await scenesStore.detachSceneFromParent(sceneToDisconnect.value)
    if (result.success) {
      buildGraph()
      dialogs.disconnectConfirm.close()
    }
  } finally {
    isDisconnecting.value = false
    sceneToDisconnect.value = null
  }
}

// ============================================
// SPOTIFY ACTIONS
// ============================================
const getSpotifyArtwork = (node: SpotifyNode): string | undefined => {
  if (!node.spotify_url) return undefined
  const cached = spotifyArtworkCache.value.get(node.id)
  if (cached !== undefined) return cached || undefined
  spotifyArtworkCache.value.set(node.id, null)
  fetchSpotifyArtwork(node)
  return undefined
}

const fetchSpotifyArtwork = async (node: SpotifyNode) => {
  if (!node.spotify_url) return
  const urlInfo = parseSpotifyUrl(node.spotify_url)
  if (!urlInfo || !['track', 'playlist', 'album', 'artist'].includes(urlInfo.type)) return
  try {
    const data = await spotifyStore.getTrackOrPlaylist(urlInfo.type as 'track' | 'playlist' | 'album' | 'artist', urlInfo.id)
    if (data) {
      let imageUrl: string | null = null
      if (urlInfo.type === 'track' && data.album?.images?.[0]) imageUrl = data.album.images[0].url
      else if (data.images?.[0]) imageUrl = data.images[0].url
      spotifyArtworkCache.value.set(node.id, imageUrl)
    }
  } catch (error) {
    logger.debug('Failed to fetch Spotify artwork:', error)
  }
}

const isSpotifyNodePlaying = (node: SpotifyNode): boolean => {
  if (!spotifyStore.isPlaying || !spotifyStore.currentTrack || !node.spotify_url) return false
  const urlInfo = parseSpotifyUrl(node.spotify_url)
  if (!urlInfo) return false
  const currentTrackId = (spotifyStore.currentTrack.uri || '').split(':').pop() || ''
  if (urlInfo.type === 'track') return urlInfo.id === currentTrackId
  const playbackContext = spotifyStore.playbackState?.context?.uri || ''
  return playbackContext.includes(urlInfo.id)
}

const openAddSpotifyDialog = () => {
  spotifyUrlInput.value = ''
  contextMenu.value.isOpen = false
  dialogs.addSpotify.open()
}

const createSpotifyNodeFromInput = async () => {
  if (!spotifyUrlInput.value.trim() || !isValidSpotifyUrl(spotifyUrlInput.value)) return
  isCreatingSpotify.value = true
  try {
    const result = await spotifyNodesStore.createSpotifyNode({
      name: 'Loading...',
      spotify_url: spotifyUrlInput.value,
      position_x: contextMenu.value.canvasX || 100,
      position_y: contextMenu.value.canvasY || 100
    })
    if (result.success) {
      dialogs.addSpotify.close()
      spotifyUrlInput.value = ''
      buildGraph()
    }
  } finally {
    isCreatingSpotify.value = false
  }
}

const openEditSpotifyDialog = (node: SpotifyNode) => {
  spotifyNodeToEdit.value = node
  editSpotifyName.value = node.name
  editSpotifyVolume.value = [(node.volume || 1) * 100]
  dialogs.editSpotify.open()
}

const confirmEditSpotify = async () => {
  if (!spotifyNodeToEdit.value || !editSpotifyName.value.trim()) return
  await spotifyNodesStore.updateSpotifyNode(spotifyNodeToEdit.value.id, {
    name: editSpotifyName.value.trim(),
    volume: editSpotifyVolume.value[0] / 100
  })
  dialogs.editSpotify.close()
  spotifyNodeToEdit.value = null
  buildGraph()
}

const confirmDeleteSpotify = (node: SpotifyNode) => {
  spotifyNodeToDelete.value = node
  dialogs.deleteSpotify.open()
}

const executeDeleteSpotify = async () => {
  if (!spotifyNodeToDelete.value) return
  isDeletingSpotify.value = true
  try {
    const result = await spotifyNodesStore.deleteSpotifyNode(spotifyNodeToDelete.value.id)
    if (result.success) {
      dialogs.deleteSpotify.close()
      spotifyNodeToDelete.value = null
      buildGraph()
    }
  } finally {
    isDeletingSpotify.value = false
  }
}

const duplicateSpotifyNode = async (node: SpotifyNode) => {
  await spotifyNodesStore.duplicateSpotifyNode(node.id)
  buildGraph()
}

const updateSpotifyVolume = async (nodeId: string, volume: number) => {
  await spotifyNodesStore.updateSpotifyNode(nodeId, { volume })
  buildGraph()
}

const updateSpotifyPlaybackTarget = async (nodeId: string, target: string) => {
  await spotifyNodesStore.updateSpotifyNode(nodeId, { playback_target: target })
  buildGraph()
}

const playSpotifyNode = async (node: SpotifyNode) => {
  if (!node.spotify_url) return
  const urlInfo = parseSpotifyUrl(node.spotify_url)
  if (urlInfo) {
    await spotifyStore.play({
      uris: urlInfo.type === 'track' ? [`spotify:track:${urlInfo.id}`] : undefined,
      context_uri: urlInfo.type !== 'track' ? `spotify:${urlInfo.type}:${urlInfo.id}` : undefined
    })
  }
}

// ============================================
// MUSIC ACTIONS
// ============================================
const isMusicPlayingOnDisplay = (node: MusicNode): boolean => {
  const targetDisplay = node.playback_target || defaultPlaybackTarget.value
  if (!targetDisplay) return false
  const displayState = displayAudioStore.displayPlayStates.get(targetDisplay)
  return displayState?.items.some(item => item.nodeId === node.id) ?? false
}

// Check if any track from a playlist is currently playing
const isPlaylistPlaying = (playlistId: string): boolean => {
  const playlist = playlistNodesStore.getPlaylistNodeById(playlistId)
  if (!playlist) return false
  
  const targetDisplay = playlist.playback_target || defaultPlaybackTarget.value
  if (!targetDisplay) return false
  
  const displayState = displayAudioStore.displayPlayStates.get(targetDisplay)
  if (!displayState) return false
  
  // Check if any item is playing from this playlist
  return displayState.items.some(item => item.playlistId === playlistId)
}

const openAddMusicDialog = () => {
  contextMenu.value.isOpen = false
  dialogs.addMusic.open()
}

const triggerMusicFileInput = () => {
  musicFileInput.value?.click()
}

const handleMusicFileSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files?.[0]) {
    await uploadMusicFile(input.files[0])
    input.value = ''
  }
}

const handleMusicFileDrop = async (event: DragEvent) => {
  const file = event.dataTransfer?.files[0]
  if (file?.type.startsWith('audio/')) await uploadMusicFile(file)
}

const uploadMusicFile = async (file: File) => {
  isCreatingMusic.value = true
  try {
    const result = await musicNodesStore.createMusicNode({
      name: file.name.replace(/\.[^/.]+$/, ''),
      file,
      position_x: contextMenu.value.canvasX || 100,
      position_y: contextMenu.value.canvasY || 100
    })
    if (result.success) {
      dialogs.addMusic.close()
      buildGraph()
    } else {
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: result.error || 'Failed to upload audio file'
      })
    }
  } catch (err) {
    toast({
      variant: 'destructive',
      title: 'Upload failed',
      description: err instanceof Error ? err.message : 'Failed to upload audio file'
    })
  } finally {
    isCreatingMusic.value = false
  }
}

const openEditMusicDialog = (node: MusicNode) => {
  musicNodeToEdit.value = node
  editMusicName.value = node.name
  editMusicVolume.value = [(node.volume || 1) * 100]
  dialogs.editMusic.open()
}

const confirmEditMusic = async () => {
  if (!musicNodeToEdit.value || !editMusicName.value.trim()) return
  await musicNodesStore.updateMusicNode(musicNodeToEdit.value.id, {
    name: editMusicName.value.trim(),
    volume: editMusicVolume.value[0] / 100
  })
  dialogs.editMusic.close()
  musicNodeToEdit.value = null
  buildGraph()
}

const confirmDeleteMusic = (node: MusicNode) => {
  musicNodeToDelete.value = node
  dialogs.deleteMusic.open()
}

const executeDeleteMusic = async () => {
  if (!musicNodeToDelete.value) return
  isDeletingMusic.value = true
  try {
    const result = await musicNodesStore.deleteMusicNode(musicNodeToDelete.value.id)
    if (result.success) {
      dialogs.deleteMusic.close()
      musicNodeToDelete.value = null
      buildGraph()
    }
  } finally {
    isDeletingMusic.value = false
  }
}

const updateMusicVolume = async (nodeId: string, volume: number) => {
  await musicNodesStore.updateMusicNode(nodeId, { volume })
  audioPlaybackStore.setMusicVolume(nodeId, volume)
  buildGraph()
}

const updateMusicPlaybackTarget = async (nodeId: string, target: string) => {
  await musicNodesStore.updateMusicNode(nodeId, { playback_target: target })
  buildGraph()
}

const toggleMusicPlayback = async (node: MusicNode) => {
  // Check if this music node is part of a playlist
  if (node.playlist_id) {
    // Get the playlist this node belongs to
    const playlist = playlistNodesStore.getPlaylistNodeById(node.playlist_id)
    if (playlist) {
      // Use the playlist's playback target
      const targetDisplay = playlist.playback_target || defaultPlaybackTarget.value
      if (!targetDisplay) return

      // Get all tracks in the playlist
      const musicNodes = musicNodesStore.getMusicNodesByPlaylistId(playlist.id)
      
      // Build full track list with URLs for the presenter
      const playlistVolume = playlist.volume || 1
      const allTracks = await Promise.all(
        musicNodes.map(async (n) => ({
          nodeId: n.id,
          name: n.name,
          url: await musicNodesStore.getAudioUrl(n.storage_path),
          volume: (n.volume || 1) * playlistVolume
        }))
      )
      
      // Filter out tracks without URLs
      const validTracks = allTracks.filter(t => t.url) as { nodeId: string; name: string; url: string; volume: number }[]
      
      // Find the index of the clicked track
      const trackIndex = validTracks.findIndex(t => t.nodeId === node.id)
      if (trackIndex < 0) return
      
      const clickedTrack = validTracks[trackIndex]
      
      // Send play-playlist-track command with crossfade
      await displayAudioStore.sendCommandToDisplay(targetDisplay, {
        type: 'play-playlist-track',
        nodeId: clickedTrack.nodeId,
        name: clickedTrack.name,
        url: clickedTrack.url,
        volume: clickedTrack.volume,
        playlistId: playlist.id,
        playlistName: playlist.name,
        trackIndex,
        totalTracks: validTracks.length,
        shuffle: playlist.shuffle,
        repeatMode: playlist.repeat_mode,
        tracks: validTracks
      })
      return
    }
  }

  // Regular music node (not in a playlist)
  const targetDisplay = node.playback_target || defaultPlaybackTarget.value
  if (!targetDisplay) return

  const displayState = displayAudioStore.displayPlayStates.get(targetDisplay)
  const isPlaying = displayState?.items.some(item => item.nodeId === node.id)

  if (isPlaying) {
    await displayAudioStore.sendCommandToDisplay(targetDisplay, { type: 'stop-music', nodeId: node.id })
  } else {
    const url = await musicNodesStore.getAudioUrl(node.storage_path)
    if (url) {
      await displayAudioStore.sendCommandToDisplay(targetDisplay, {
        type: 'play-music',
        nodeId: node.id,
        name: node.name,
        url,
        volume: node.volume,
        loop: node.loop
      })
    }
  }
}

const toggleMusicLoop = async (node: MusicNode) => {
  const newLoop = node.loop === false ? true : false
  await musicNodesStore.updateMusicNode(node.id, { loop: newLoop })
  const playingItem = audioPlaybackStore.playingMusicItems.get(node.id)
  if (playingItem) {
    playingItem.audio.loop = newLoop
    playingItem.loop = newLoop
  }
  buildGraph()
}

const duplicateMusicNode = async (node: MusicNode) => {
  await musicNodesStore.duplicateMusicNode(node.id)
  buildGraph()
}

// ============================================
// PLAYLIST ACTIONS
// ============================================
const openAddPlaylistDialog = () => {
  playlistNameInput.value = ''
  contextMenu.value.isOpen = false
  dialogs.addPlaylist.open()
}

const createPlaylistFromInput = async () => {
  if (!playlistNameInput.value.trim()) return
  isCreatingPlaylist.value = true
  try {
    const result = await playlistNodesStore.createPlaylistNode({
      name: playlistNameInput.value.trim(),
      position_x: contextMenu.value.canvasX || 100,
      position_y: contextMenu.value.canvasY || 100
    })
    if (result.success) {
      dialogs.addPlaylist.close()
      playlistNameInput.value = ''
      buildGraph()
    }
  } finally {
    isCreatingPlaylist.value = false
  }
}

// Helper to get music nodes inside a playlist
const getMusicNodesInPlaylist = (playlistId: string) => {
  return musicNodesStore.getMusicNodesByPlaylistId(playlistId)
}

const renamePlaylist = async (id: string, name: string) => {
  await playlistNodesStore.updatePlaylistNode(id, { name })
  buildGraph()
}

const onPlaylistToggleCollapse = async (playlistId: string) => {
  const playlist = playlistNodesStore.getPlaylistNodeById(playlistId)
  if (playlist) {
    await playlistNodesStore.updatePlaylistNode(playlistId, { is_collapsed: !playlist.is_collapsed })
    buildGraph()
  }
}

const confirmDeletePlaylist = (id: string) => {
  const node = playlistNodesStore.getPlaylistNodeById(id)
  if (node) {
    playlistNodeToDelete.value = node
    dialogs.deletePlaylist.open()
  }
}

const executeDeletePlaylist = async () => {
  if (!playlistNodeToDelete.value) return
  isDeletingPlaylist.value = true
  try {
    // First, unset playlist_id for all music nodes in this playlist
    const musicNodes = getMusicNodesInPlaylist(playlistNodeToDelete.value.id)
    for (const musicNode of musicNodes) {
      await musicNodesStore.updateMusicNode(musicNode.id, { playlist_id: null })
    }
    
    const result = await playlistNodesStore.deletePlaylistNode(playlistNodeToDelete.value.id)
    if (result.success) {
      dialogs.deletePlaylist.close()
      playlistNodeToDelete.value = null
      buildGraph()
    }
  } finally {
    isDeletingPlaylist.value = false
  }
}

const duplicatePlaylistNode = async (id: string) => {
  await playlistNodesStore.duplicatePlaylistNode(id)
  // Also need to fetch music nodes to get the new ones
  await musicNodesStore.fetchMusicNodes()
  buildGraph()
}

const togglePlaylistShuffle = async (id: string) => {
  await playlistNodesStore.toggleShuffle(id)
  buildGraph()
}

const setPlaylistRepeatMode = async (id: string, mode: 'none' | 'all' | 'one') => {
  await playlistNodesStore.setRepeatMode(id, mode)
  buildGraph()
}

const updatePlaylistPlaybackTarget = async (id: string, target: string) => {
  await playlistNodesStore.updatePlaylistNode(id, { playback_target: target })
  buildGraph()
}

const togglePlaylistPlayback = async (id: string) => {
  const playlist = playlistNodesStore.getPlaylistNodeById(id)
  if (!playlist) {
    logger.warn('Playlist not found:', id)
    return
  }

  const targetDisplay = playlist.playback_target || defaultPlaybackTarget.value
  if (!targetDisplay) {
    logger.warn('No playback target for playlist')
    return
  }

  // Get all music nodes in this playlist
  const musicNodes = musicNodesStore.getMusicNodesByPlaylistId(id)
  if (musicNodes.length === 0) {
    logger.info('Playlist is empty, nothing to play')
    return
  }

  // Check if any tracks from this playlist are currently playing
  const displayState = displayAudioStore.displayPlayStates.get(targetDisplay)
  const playlistNodeIds = new Set(musicNodes.map(n => n.id))
  const playingTracksInPlaylist = displayState?.items.filter(item => playlistNodeIds.has(item.nodeId)) || []

  if (playingTracksInPlaylist.length > 0) {
    // Stop all tracks in this playlist
    for (const track of playingTracksInPlaylist) {
      await displayAudioStore.sendCommandToDisplay(targetDisplay, { 
        type: 'stop-music', 
        nodeId: track.nodeId 
      })
    }
    logger.info('Stopped playlist playback:', playlist.name)
  } else {
    // Start playing the playlist - play first track only
    // Build full track list with URLs for presenter
    const playlistVolume = playlist.volume || 1
    const allTracks = await Promise.all(
      musicNodes.map(async (node) => {
        const url = await musicNodesStore.getAudioUrl(node.storage_path)
        return {
          nodeId: node.id,
          name: node.name,
          url: url || '',
          volume: node.volume * playlistVolume
        }
      })
    )
    
    // Filter out tracks without URLs
    const validTracks = allTracks.filter(t => t.url)
    if (validTracks.length === 0) {
      logger.warn('No valid tracks in playlist')
      return
    }

    // Play first track with playlist context and full track list
    const firstTrack = validTracks[0]
    await displayAudioStore.sendCommandToDisplay(targetDisplay, {
      type: 'play-music',
      nodeId: firstTrack.nodeId,
      name: firstTrack.name,
      url: firstTrack.url,
      volume: firstTrack.volume,
      loop: playlist.repeat_mode === 'one', // Only loop single track if repeat_mode is 'one'
      // Playlist context
      playlistId: playlist.id,
      playlistName: playlist.name,
      trackIndex: 0,
      totalTracks: validTracks.length,
      shuffle: playlist.shuffle,
      repeatMode: playlist.repeat_mode,
      // Full track list for prev/next navigation
      tracks: validTracks
    })
    logger.info('Started playlist playback:', playlist.name, `- playing: ${firstTrack.name}`)
  }
}

// ============================================
// CONTAINER ACTIONS
// ============================================
const containerToDelete = ref<GraphContainer | null>(null)

const onContainerResize = async (containerId: string, width: number, height: number) => {
  await graphContainersStore.updateContainer(containerId, { width, height })
  
  // Remove nodes that are now outside the container bounds
  const container = graphContainersStore.getContainerById(containerId)
  if (container) {
    const headerHeight = 50 // Container header height
    const padding = 16 // Content padding
    
    // Check scenes inside this container
    const scenesInContainer = Array.from(scenesStore.scenes.values()).filter((s: Scene) => s.container_id === containerId)
    for (const scene of scenesInContainer) {
      const nodeWidth = 180 // Approximate scene node width
      const nodeHeight = 100 // Approximate scene node height
      const posX = scene.graph_position_x ?? 0
      const posY = scene.graph_position_y ?? 0
      
      const isOutside = 
        posX < 0 ||
        posY < 0 ||
        posX + nodeWidth > width - padding ||
        posY + nodeHeight > height - headerHeight - padding
      
      if (isOutside) {
        const absoluteX = container.position_x + posX
        const absoluteY = container.position_y + posY
        await scenesStore.updateScenePosition(scene.id, absoluteX, absoluteY)
        await scenesStore.updateSceneContainer(scene.id, null, { x: absoluteX, y: absoluteY })
        logger.info(`Removed scene '${scene.name}' from container - outside bounds`)
      }
    }
    
    // Check music nodes inside this container
    const musicInContainer = musicNodesStore.allMusicNodes.filter(n => n.container_id === containerId)
    for (const node of musicInContainer) {
      const nodeWidth = 150
      const nodeHeight = 80
      
      const isOutside = 
        node.position_x < 0 ||
        node.position_y < 0 ||
        node.position_x + nodeWidth > width - padding ||
        node.position_y + nodeHeight > height - headerHeight - padding
      
      if (isOutside) {
        const absoluteX = container.position_x + node.position_x
        const absoluteY = container.position_y + node.position_y
        await musicNodesStore.updateMusicNode(node.id, { 
          container_id: null,
          position_x: absoluteX,
          position_y: absoluteY
        })
        logger.info(`Removed music node '${node.name}' from container - outside bounds`)
      }
    }
    
    // Check spotify nodes inside this container
    const spotifyInContainer = spotifyNodesStore.allSpotifyNodes.filter(n => n.container_id === containerId)
    for (const node of spotifyInContainer) {
      const nodeWidth = 180
      const nodeHeight = 100
      
      const isOutside = 
        node.position_x < 0 ||
        node.position_y < 0 ||
        node.position_x + nodeWidth > width - padding ||
        node.position_y + nodeHeight > height - headerHeight - padding
      
      if (isOutside) {
        const absoluteX = container.position_x + node.position_x
        const absoluteY = container.position_y + node.position_y
        await spotifyNodesStore.updateSpotifyNode(node.id, { 
          container_id: null,
          position_x: absoluteX,
          position_y: absoluteY
        })
        logger.info(`Removed spotify node '${node.name}' from container - outside bounds`)
      }
    }
    
    // Check playlist nodes inside this container
    const playlistsInContainer = playlistNodesStore.allPlaylistNodes.filter(n => n.container_id === containerId)
    for (const node of playlistsInContainer) {
      const nodeWidth = node.width || 300
      const nodeHeight = node.height || 250
      
      const isOutside = 
        node.position_x < 0 ||
        node.position_y < 0 ||
        node.position_x + nodeWidth > width - padding ||
        node.position_y + nodeHeight > height - headerHeight - padding
      
      if (isOutside) {
        const absoluteX = container.position_x + node.position_x
        const absoluteY = container.position_y + node.position_y
        await playlistNodesStore.updatePlaylistNode(node.id, { 
          container_id: null,
          position_x: absoluteX,
          position_y: absoluteY
        })
        logger.info(`Removed playlist node '${node.name}' from container - outside bounds`)
      }
    }
  }
  
  buildGraph()
}

const onContainerRename = async (containerId: string, name: string) => {
  await graphContainersStore.updateContainer(containerId, { name })
  buildGraph()
}

const onContainerColorChange = async (containerId: string, color: string) => {
  await graphContainersStore.updateContainer(containerId, { color })
  buildGraph()
}

const onContainerBackgroundImageChange = async (containerId: string, imagePath: string | null) => {
  await graphContainersStore.updateContainer(containerId, { background_image_path: imagePath })
  buildGraph()
}

const onContainerToggleCollapse = async (containerId: string) => {
  const container = graphContainersStore.getContainerById(containerId)
  if (container) {
    await graphContainersStore.updateContainer(containerId, { is_collapsed: !container.is_collapsed })
    buildGraph()
  }
}

const onContainerDelete = (containerId: string) => {
  const container = graphContainersStore.getContainerById(containerId)
  if (container) {
    containerToDelete.value = container
    dialogs.deleteContainer.open()
  }
}

const executeDeleteContainer = async () => {
  if (!containerToDelete.value) return
  await graphContainersStore.deleteContainer(containerToDelete.value.id)
  dialogs.deleteContainer.close()
  containerToDelete.value = null
  buildGraph()
}

const createContainerAtPosition = async () => {
  logger.info('📦 Creating container at position:', contextMenu.value.canvasX, contextMenu.value.canvasY)
  const position = { x: contextMenu.value.canvasX, y: contextMenu.value.canvasY }
  const result = await graphContainersStore.createContainer({
    name: 'Container',
    position_x: position.x,
    position_y: position.y,
    width: 400,
    height: 300,
    color: 'transparent' // Default to no color
  })
  logger.info('📦 Container creation result:', result)
  contextMenu.value.isOpen = false
  buildGraph()
}

// ============================================
// AUDIO-SCENE LINKING
// ============================================
const linkSpotifyToScene = async (spotifyNodeId: string, sceneId: string) => {
  // Get the spotify node to get its URL
  const spotifyNode = spotifyNodesStore.getSpotifyNodeById(spotifyNodeId)
  const spotifyUrl = spotifyNode?.spotify_url || ''
  
  await sceneSpotifyConfigsStore.linkToScene(spotifyNodeId, sceneId, spotifyUrl)
  await fetchAudioSceneLinks()
}

const unlinkSpotifyFromScene = async (spotifyNodeId: string, sceneId: string) => {
  await sceneSpotifyConfigsStore.unlinkFromScene(spotifyNodeId, sceneId)
  await fetchAudioSceneLinks()
  buildGraph()
}

const linkMusicToScene = async (musicNodeId: string, sceneId: string) => {
  await sceneMusicConfigsStore.linkToScene(musicNodeId, sceneId)
  await fetchAudioSceneLinks()
}

const unlinkMusicFromScene = async (musicNodeId: string, sceneId: string) => {
  await sceneMusicConfigsStore.unlinkFromScene(musicNodeId, sceneId)
  await fetchAudioSceneLinks()
  buildGraph()
}

const linkPlaylistToScene = async (playlistNodeId: string, sceneId: string) => {
  await scenePlaylistConfigsStore.linkToScene(playlistNodeId, sceneId)
  await fetchAudioSceneLinks()
}

const unlinkPlaylistFromScene = async (playlistNodeId: string, sceneId: string) => {
  await scenePlaylistConfigsStore.unlinkFromScene(playlistNodeId, sceneId)
  await fetchAudioSceneLinks()
  buildGraph()
}

const fetchAudioSceneLinks = async () => {
  // Fetch all configs from the stores
  await Promise.all([
    sceneSpotifyConfigsStore.fetchAllConfigs(),
    sceneMusicConfigsStore.fetchAllConfigs(),
    scenePlaylistConfigsStore.fetchAllConfigs()
  ])
  
  // Build the links from store data
  const spotifyConfigs = Array.from(sceneSpotifyConfigsStore.configs.values())
  const musicConfigs = Array.from(sceneMusicConfigsStore.configs.values())
  const playlistConfigs = Array.from(scenePlaylistConfigsStore.configs.values())
  
  audioSceneLinks.value = [
    ...spotifyConfigs.map(l => ({ id: l.id, scene_id: l.scene_id, audio_node_id: l.spotify_node_id, type: 'spotify' as const })),
    ...musicConfigs.map(l => ({ id: l.id, scene_id: l.scene_id, audio_node_id: l.music_node_id, type: 'music' as const })),
    ...playlistConfigs.map(l => ({ id: l.id, scene_id: l.scene_id, audio_node_id: l.playlist_node_id, type: 'playlist' as const }))
  ]
}

// ============================================
// ZOOM & PASTE
// ============================================
const zoomIn = () => vueFlowZoomIn()
const zoomOut = () => vueFlowZoomOut()
const fitViewToNodes = () => fitView({ padding: 0.2, duration: 300 })

const zoomToNode = (nodeId: string) => {
  const node = findNode(nodeId)
  if (node) {
    const width = node.dimensions?.width || 200
    const height = node.dimensions?.height || 100
    
    // For larger nodes (containers, playlists), use fitView to show the whole node
    // For smaller nodes, center with a reasonable zoom
    if (width > 300 || height > 200 || nodeId.startsWith('container-') || nodeId.startsWith('playlist-')) {
      // Use fitView to fit the node nicely in the viewport
      fitView({ 
        nodes: [node.id], 
        padding: 0.3, 
        duration: 300,
        maxZoom: 1.2 // Don't zoom in too much
      })
    } else {
      // For smaller nodes, center with a comfortable zoom level
      const x = node.position.x + width / 2
      const y = node.position.y + height / 2
      setCenter(x, y, { zoom: 1.2, duration: 300 })
    }
  }
}

// Search ref for keyboard shortcut
const graphSearchRef = ref<InstanceType<typeof GraphSearch> | null>(null)

const handleSearchActivate = async (result: { type: string; id: string; item: unknown }) => {
  if (result.type === 'scene') {
    await activateScene(result.id)
  } else if (result.type === 'spotify') {
    const node = result.item as SpotifyNode
    await playSpotifyNode(node)
  } else if (result.type === 'music') {
    const node = result.item as MusicNode
    await toggleMusicPlayback(node)
  } else if (result.type === 'playlist') {
    // Play the playlist
    await togglePlaylistPlayback(result.id)
  }
}

// Keyboard shortcut for search (Cmd+F / Ctrl+F)
const handleKeydown = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
    event.preventDefault()
    graphSearchRef.value?.openSearch()
  }
}

const handlePaste = async (event: ClipboardEvent) => {
  const text = event.clipboardData?.getData('text')
  if (text && isValidSpotifyUrl(text)) {
    event.preventDefault()
    const flowCoordinates = project({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
    const result = await spotifyNodesStore.createSpotifyNode({
      name: 'Loading...',
      spotify_url: text,
      position_x: flowCoordinates.x,
      position_y: flowCoordinates.y
    })
    if (result.success) buildGraph()
  }
}

// ============================================
// GLOBAL DRAG & DROP
// ============================================
const handleGlobalDragOver = (event: DragEvent) => {
  // Check if dragging files, text (Spotify URL), or library files
  const hasFiles = event.dataTransfer?.types.includes('Files')
  const hasText = event.dataTransfer?.types.includes('text/plain') || event.dataTransfer?.types.includes('text/uri-list')
  
  if (hasFiles || hasText) {
    event.preventDefault()
    event.stopPropagation()
    
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
    
    // Determine drag type for visual feedback
    if (hasFiles) {
      dragType.value = 'audio'
    } else {
      dragType.value = 'spotify'
    }
    isDraggingOver.value = true
  }
}

const handleGlobalDragLeave = (event: DragEvent) => {
  // Only hide if leaving the container entirely
  const relatedTarget = event.relatedTarget as HTMLElement | null
  const container = event.currentTarget as HTMLElement
  if (!relatedTarget || !container.contains(relatedTarget)) {
    isDraggingOver.value = false
    dragType.value = null
  }
}

const handleGlobalDrop = async (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()
  isDraggingOver.value = false
  dragType.value = null
  
  // Get drop position in flow coordinates
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const flowCoordinates = project({ 
    x: event.clientX - rect.left, 
    y: event.clientY - rect.top 
  })
  
  // Check for audio files
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    for (const file of Array.from(files)) {
      if (file.type.startsWith('audio/')) {
        isCreatingMusic.value = true
        try {
          const result = await musicNodesStore.createMusicNode({
            name: file.name.replace(/\.[^/.]+$/, ''),
            file,
            position_x: flowCoordinates.x,
            position_y: flowCoordinates.y
          })
          if (result.success) {
            buildGraph()
          } else {
            toast({
              variant: 'destructive',
              title: 'Upload failed',
              description: result.error || 'Failed to upload audio file'
            })
          }
        } catch (err) {
          toast({
            variant: 'destructive',
            title: 'Upload failed',
            description: err instanceof Error ? err.message : 'Failed to upload audio file'
          })
        } finally {
          isCreatingMusic.value = false
        }
      }
    }
    return
  }
  
  // Check for Spotify URLs (text or uri-list)
  const text = event.dataTransfer?.getData('text/plain') || event.dataTransfer?.getData('text/uri-list')
  if (text) {
    // Extract URL from potential multi-line or formatted text
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
    for (const line of lines) {
      if (isValidSpotifyUrl(line)) {
        const result = await spotifyNodesStore.createSpotifyNode({
          name: 'Loading...',
          spotify_url: line,
          position_x: flowCoordinates.x,
          position_y: flowCoordinates.y
        })
        if (result.success) buildGraph()
        break // Only create one node per drop
      }
    }
  }
}

// ============================================
// WATCHERS
// ============================================
watch(() => scenesStore.scenes.size, () => buildGraph())

watch(() => scenesStore.getActiveScene?.id, async (newActiveId, oldActiveId) => {
  if (newActiveId && newActiveId !== oldActiveId) {
    buildGraph()
    const currentRouteSceneId = router.currentRoute.value.params.sceneId
    if (currentRouteSceneId !== newActiveId) {
      router.push({ name: 'scene', params: { sceneId: newActiveId } })
    }
  } else if (!newActiveId && oldActiveId) {
    buildGraph()
  }
})

watch(() => router.currentRoute.value.params.sceneId, (newSceneId) => {
  if (newSceneId && typeof newSceneId === 'string') buildGraph()
})

// ============================================
// LIFECYCLE
// ============================================
onMounted(async () => {
  console.time('[SceneGraphView] Total Init')
  
  // PHASE 1: Load essential data for scene graph rendering (fast)
  console.time('[SceneGraphView] Essential Data')
  await Promise.all([
    scenesStore.scenes.size === 0 ? scenesStore.fetchUserScenes() : Promise.resolve(),
    displaysStore.fetchUserDisplays(),
    graphContainersStore.fetchContainers(),
  ])
  console.timeEnd('[SceneGraphView] Essential Data')
  console.log('[SceneGraphView] Scenes loaded:', scenesStore.scenes.size)
  
  // Build graph immediately with scenes - don't wait for audio nodes
  console.time('[SceneGraphView] First buildGraph')
  debouncedBuildGraph(true)
  console.timeEnd('[SceneGraphView] First buildGraph')
  
  // PHASE 2: Load audio nodes in background and rebuild graph when done
  console.time('[SceneGraphView] Audio Data (background)')
  Promise.all([
    spotifyNodesStore.fetchSpotifyNodes(),
    musicNodesStore.fetchMusicNodes(),
    playlistNodesStore.fetchPlaylistNodes(),
    fetchAudioSceneLinks()
  ]).then(() => {
    console.timeEnd('[SceneGraphView] Audio Data (background)')
    // Rebuild graph to include audio nodes
    debouncedBuildGraph(false)
  }).catch(err => {
    console.error('[SceneGraphView] Audio data fetch failed:', err)
  })
  
  // Let VueFlow render and recalculate positions
  await nextTick()
  debouncedBuildGraph(true)
  
  await nextTick()
  const nodeIds = getNodes.value.map(n => n.id)
  if (nodeIds.length > 0) {
    updateNodeInternals(nodeIds)
  }
  
  console.timeEnd('[SceneGraphView] Total Init')
  
  document.addEventListener('paste', handlePaste)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('paste', handlePaste)
  document.removeEventListener('keydown', handleKeydown)
  audioPlaybackStore.stopAll()
})
</script>

<template>
  <div 
    class="flex h-full bg-transparent"
    @dragover="handleGlobalDragOver"
    @dragleave="handleGlobalDragLeave"
    @drop="handleGlobalDrop"
  >
    <div class="scene-graph-view flex-1 flex flex-col">
      <div class="flex-1 relative">
        <!-- Drop Zone Overlay -->
        <Transition name="fade">
          <div
            v-if="isDraggingOver"
            class="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm border-2 border-dashed rounded-lg pointer-events-none"
            :class="dragType === 'spotify' ? 'border-green-500' : 'border-primary'"
          >
            <div class="flex flex-col items-center gap-3 text-center">
              <div 
                class="w-16 h-16 rounded-full flex items-center justify-center"
                :class="dragType === 'spotify' ? 'bg-green-500/20' : 'bg-primary/20'"
              >
                <svg v-if="dragType === 'spotify'" class="w-8 h-8 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
                <svg v-else class="w-8 h-8 text-primary" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 18V5l12-2v13"/>
                  <circle cx="6" cy="18" r="3"/>
                  <circle cx="18" cy="16" r="3"/>
                </svg>
              </div>
              <div>
                <p class="text-lg font-medium" :class="dragType === 'spotify' ? 'text-green-500' : 'text-primary'">
                  {{ dragType === 'spotify' ? 'Drop Spotify Link' : 'Drop Audio File' }}
                </p>
                <p class="text-sm text-muted-foreground">
                  {{ dragType === 'spotify' ? 'Create a new Spotify node' : 'Upload and create a music node' }}
                </p>
              </div>
            </div>
          </div>
        </Transition>
        
        <!-- Long-press wrapper for mobile context menu -->
        <div
          class="vue-flow-wrapper w-full h-full"
          @touchstart.passive="onPaneTouchStart"
          @touchmove.passive="onPaneTouchMove"
          @touchend="onPaneTouchEnd"
          @touchcancel="onPaneTouchEnd"
        >
          <VueFlow
            v-model:nodes="nodes"
            v-model:edges="edges"
            :default-viewport="savedSettings.viewport || { zoom: isMobile ? 0.5 : 1 }"
          :min-zoom="0.2"
          :max-zoom="4"
          :snap-to-grid="true"
          :snap-grid="[16, 16]"
          :pan-on-scroll="true"
          :pan-on-drag="true"
          :zoom-on-scroll="true"
          :zoom-on-pinch="true"
          :prevent-scrolling="true"
          :connection-line-type="ConnectionLineType.Step"
          :default-edge-options="{ type: 'step' }"
          @nodes-change="onNodesChange"
          @edges-change="onEdgesChange"
          @connect="onConnect"
          @node-drag-stop="onNodeDragStop"
          @node-drag="onNodeDrag"
          @pane-context-menu="onCanvasRightClick"
          @edge-click="onEdgeClick"
          @viewport-change="debouncedSaveSettings"
          @contextmenu.prevent
          class="scene-flow"
        >
          <!-- Scene Node -->
          <template #node-scene="{ data }">
            <SceneNode
              :scene="data.scene"
              :preview="data.preview"
              :is-mobile="isMobile"
              :all-scenes="allScenesArray"
              :all-music-nodes="musicNodesStore.allMusicNodes"
              :all-playlist-nodes="playlistNodesStore.allPlaylistNodes"
              :linked-music-node-ids="getLinkedMusicNodeIds(data.scene.id)"
              :linked-playlist-node-ids="getLinkedPlaylistNodeIds(data.scene.id)"
              @activate="activateScene"
              @create-child="createChildScene"
              @edit="openEditSceneDialog"
              @duplicate="duplicateScene"
              @delete="confirmDeleteScene"
              @set-parent="setSceneParent"
              @link-music="linkMusicToSceneFromMenu"
              @link-playlist="linkPlaylistToSceneFromMenu"
            />
          </template>

          <!-- Spotify Node -->
          <template #node-spotify="{ data }">
            <SpotifyNodeComponent
              :node="data.node"
              :is-playing="isSpotifyNodePlaying(data.node)"
              :artwork-url="getSpotifyArtwork(data.node)"
              :is-mobile="isMobile"
              :displays="displaysStore.allDisplays"
              :default-playback-target="defaultPlaybackTarget"
              @play="playSpotifyNode"
              @edit="openEditSpotifyDialog"
              @delete="confirmDeleteSpotify"
              @duplicate="duplicateSpotifyNode"
              @update-volume="updateSpotifyVolume"
              @update-playback-target="updateSpotifyPlaybackTarget"
            />
          </template>

          <!-- Music Node -->
          <template #node-music="{ data }">
            <MusicNodeComponent
              :node="data.node"
              :is-playing="isMusicPlayingOnDisplay(data.node)"
              :is-mobile="isMobile"
              :displays="displaysStore.allDisplays"
              :default-playback-target="defaultPlaybackTarget"
              @toggle-playback="toggleMusicPlayback"
              @edit="openEditMusicDialog"
              @delete="confirmDeleteMusic"
              @duplicate="duplicateMusicNode"
              @toggle-loop="toggleMusicLoop"
              @update-volume="updateMusicVolume"
              @update-playback-target="updateMusicPlaybackTarget"
            />
          </template>

          <!-- Playlist Node (container-like for music nodes) -->
          <template #node-playlist="{ data }">
            <PlaylistNodeComponent
              :data="data"
              :is-playing="isPlaylistPlaying(data.id)"
              :is-mobile="isMobile"
              :displays="displaysStore.allDisplays"
              :default-playback-target="defaultPlaybackTarget"
              :track-count="getMusicNodesInPlaylist(data.id).length"
              @delete="confirmDeletePlaylist"
              @duplicate="duplicatePlaylistNode"
              @rename="renamePlaylist"
              @toggle-playback="togglePlaylistPlayback"
              @toggle-shuffle="togglePlaylistShuffle"
              @set-repeat-mode="setPlaylistRepeatMode"
              @update-playback-target="updatePlaylistPlaybackTarget"
              @toggle-collapse="onPlaylistToggleCollapse"
            />
          </template>

          <!-- Container Node -->
          <template #node-container="{ data }">
            <ContainerNodeComponent
              :data="data"
              :is-mobile="isMobile"
              @resize="onContainerResize"
              @delete="onContainerDelete"
              @rename="onContainerRename"
              @change-color="onContainerColorChange"
              @change-background-image="onContainerBackgroundImageChange"
              @toggle-collapse="onContainerToggleCollapse"
            />
          </template>
          </VueFlow>
        </div>

        <!-- Zoom Controls -->
        <GraphZoomControls
          :connection-mode="connectionMode"
          @zoom-in="zoomIn"
          @zoom-out="zoomOut"
          @fit-view="fitViewToNodes"
          @cycle-connection-mode="cycleConnectionMode"
        />

        <!-- Search -->
        <div class="absolute bottom-4 left-20 z-10">
          <GraphSearch
            ref="graphSearchRef"
            @zoom-to-node="zoomToNode"
            @activate-item="handleSearchActivate"
          />
        </div>

        <!-- Context Menu -->
        <GraphContextMenu
          v-model:is-open="contextMenu.isOpen"
          :x="contextMenu.x"
          :y="contextMenu.y"
          @create-scene="createSceneAtPosition"
          @add-spotify="openAddSpotifyDialog"
          @add-music="openAddMusicDialog"
          @add-playlist="openAddPlaylistDialog"
          @create-container="createContainerAtPosition"
        />
      </div>
    </div>

    <!-- Hidden file input for music upload -->
    <input
      ref="musicFileInput"
      type="file"
      accept="audio/*"
      class="hidden"
      @change="handleMusicFileSelect"
    />

    <!-- All Dialogs -->
    <GraphDialogs
      :is-mobile="isMobile"
      :dialogs="dialogs"
      :child-scene-name="childSceneName"
      :parent-scene-name="parentSceneName"
      :descendant-count="descendantCount"
      :is-connecting="isConnecting"
      :scene-to-disconnect-name="sceneToDisconnectName"
      :descendant-count-to-disconnect="descendantCountToDisconnect"
      :is-disconnecting="isDisconnecting"
      :scene-to-delete="sceneToDelete"
      :edit-scene-name="editSceneName"
      :edit-scene-description="editSceneDescription"
      :is-editing-scene="isEditingScene"
      :is-deleting-scene="isDeletingScene"
      :spotify-url-input="spotifyUrlInput"
      :spotify-node-to-delete="spotifyNodeToDelete"
      :edit-spotify-name="editSpotifyName"
      :edit-spotify-volume="editSpotifyVolume"
      :is-creating-spotify="isCreatingSpotify"
      :is-deleting-spotify="isDeletingSpotify"
      :music-node-to-delete="musicNodeToDelete"
      :edit-music-name="editMusicName"
      :edit-music-volume="editMusicVolume"
      :is-creating-music="isCreatingMusic"
      :is-deleting-music="isDeletingMusic"
      :playlist-name-input="playlistNameInput"
      :playlist-node-to-delete="playlistNodeToDelete"
      :is-creating-playlist="isCreatingPlaylist"
      :is-deleting-playlist="isDeletingPlaylist"
      :container-to-delete="containerToDelete"
      @update:edit-scene-name="editSceneName = $event"
      @update:edit-scene-description="editSceneDescription = $event"
      @update:spotify-url-input="spotifyUrlInput = $event"
      @update:edit-spotify-name="editSpotifyName = $event"
      @update:edit-spotify-volume="editSpotifyVolume = $event"
      @update:edit-music-name="editMusicName = $event"
      @update:edit-music-volume="editMusicVolume = $event"
      @update:playlist-name-input="playlistNameInput = $event"
      @confirm-connect="confirmConnect"
      @confirm-disconnect="confirmDisconnect"
      @confirm-edit-scene="confirmEditScene"
      @execute-delete-scene="executeDeleteScene"
      @create-spotify-node="createSpotifyNodeFromInput"
      @confirm-edit-spotify="confirmEditSpotify"
      @execute-delete-spotify="executeDeleteSpotify"
      @trigger-music-file-input="triggerMusicFileInput"
      @handle-music-file-drop="handleMusicFileDrop"
      @confirm-edit-music="confirmEditMusic"
      @execute-delete-music="executeDeleteMusic"
      @create-playlist-node="createPlaylistFromInput"
      @execute-delete-playlist="executeDeletePlaylist"
      @execute-delete-container="executeDeleteContainer"
    />
  </div>
</template>

<style scoped>
.scene-graph-view {
  background: transparent;
}

.scene-flow {
  background: transparent;
}

/* Edge styling */
:deep(.vue-flow__edge-path) {
  stroke-width: 2;
}

:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: hsl(var(--primary));
  stroke-width: 3;
}

/* Edge label (X button) styling - clean simple icon */
:deep(.vue-flow__edge-textbg) {
  fill: hsl(var(--background)) !important;
  stroke: none !important;
  rx: 10 !important;
  ry: 10 !important;
  width: 24px !important;
  height: 24px !important;
}

:deep(.vue-flow__edge-text) {
  fill: hsl(var(--muted-foreground)) !important;
  font-size: 14px !important;
  font-weight: 500;
  cursor: pointer;
  dominant-baseline: central !important;
  text-anchor: middle !important;
}

:deep(.vue-flow__edge:hover .vue-flow__edge-text) {
  fill: hsl(var(--destructive)) !important;
}

:deep(.vue-flow__edge:hover .vue-flow__edge-textbg) {
  fill: hsl(var(--destructive) / 0.1) !important;
}

/* Remove any dashed border on selection */
:deep(.vue-flow__edge.selected .vue-flow__edge-textbg),
:deep(.vue-flow__edge .vue-flow__edge-textbg) {
  stroke: none !important;
  stroke-dasharray: none !important;
}

/* Handle styling */
:deep(.vue-flow__handle) {
  width: 10px;
  height: 10px;
  background: hsl(var(--primary));
  border: 2px solid hsl(var(--card));
}

@media (max-width: 640px) {
  :deep(.vue-flow__handle) {
    width: 16px;
    height: 16px;
  }
}

:deep(.vue-flow__handle:hover) {
  background: hsl(var(--primary) / 0.8);
}

/* Drop zone fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
